import { useMemo, useState } from "react";
import { Box, Checkbox, FormControlLabel } from "@mui/material";

import { triggerDownload } from "@thalamos/frontend";
import type {
  ExternalPatientLinkEnhanced,
  FormContextData,
  FormContextMiniForm,
  SelectedFormsObject,
} from "@thalamos/aspire-common";
import {
  formIsApplication,
  formIsRecordOfDetention,
  getBaseFormTemplate,
} from "@thalamos/aspire-common";

import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { FormCheckboxRow } from "~/pages/FormDraft/AdmissionFormCheckboxRow.js";
import { FormContextPdfViewer } from "~/pages/FormProgressPage/helpers/FormContextPdfViewer.js";

export const SelectFormsDialog = ({
  open,
  onClose,
  forms,
  formContext,
  uploadFormPdfViaExternalPatientLink,
  uploadToRioExternalLink,
  mode,
}: {
  open: boolean;
  onClose: () => void;
  forms: FormContextMiniForm[];
  formContext: FormContextData;
  uploadFormPdfViaExternalPatientLink?: (
    externalLink: ExternalPatientLinkEnhanced,
    formIds: string[],
    formContext: FormContextData,
  ) => Promise<void>;
  uploadToRioExternalLink?: ExternalPatientLinkEnhanced | null | undefined;
  mode: "download" | "upload";
}) => {
  const [selectedForms, setSelectedForms] = useState<SelectedFormsObject[]>([]);
  const [pdfViewFormId, setPdfViewFormId] = useState<null | string>(null);

  const h3 = useMemo(
    () => forms.find((f) => formIsRecordOfDetention(f.formTemplate)),
    [forms],
  );

  const applicationIds = useMemo(
    () =>
      forms.filter((f) => formIsApplication(f.formTemplate)).map((f) => f.id),
    [forms],
  );

  const h3NotSelected = useMemo(
    () => h3 && !selectedForms.some((s) => s.id.includes(h3.id)),
    [selectedForms, h3],
  );

  const noApplicationSelected = useMemo(
    () =>
      applicationIds.length > 0 &&
      !selectedForms.some((s) => applicationIds.includes(s.id)),
    [selectedForms, applicationIds],
  );

  const moreThanOneApplicationSelected = useMemo(
    () =>
      applicationIds.length > 1 &&
      selectedForms.filter((s) => applicationIds.includes(s.id)).length > 1,
    [selectedForms, applicationIds],
  );

  const selectedSections = useMemo(
    () => selectedForms.map((s) => getBaseFormTemplate(s.templateId)?.section),
    [selectedForms],
  );

  const differentSectionsSelected = useMemo(
    () =>
      (selectedSections.includes("2") && selectedSections.includes("3")) ||
      (selectedSections.includes("3") && selectedSections.includes("4")),
    [selectedSections],
  );

  const showWarningBanner = mode === "upload";

  return (
    <PopupDialog open={open} maxWidth="lg">
      <PopupDialogTitle
        closeDialog={onClose}
        titleText={`Select Forms to ${mode === "upload" ? "Upload to Rio" : "Download"}`}
      ></PopupDialogTitle>
      <Banner
        body={`The forms will be ${mode === "upload" ? "uploaded" : "downloaded"} in the order they are selected. For example, the form that is selected first, will show first.`}
        bannerType={BannerList.INFO}
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        label="Select All"
        sx={{ mb: 2 }}
        control={
          <Checkbox
            checked={selectedForms.length === forms.length}
            sx={{ color: "primary.main" }}
            onChange={(e) => {
              if (e.target.checked) {
                const unselectedForms = forms
                  .filter(
                    (form) => !selectedForms.some((s) => s.id === form.id),
                  )
                  .map((form) => ({
                    id: form.id,
                    templateId: form.formTemplate.id,
                  }));
                setSelectedForms([...selectedForms, ...unselectedForms]);
              } else {
                setSelectedForms([]);
              }
            }}
          />
        }
      />
      <Box>
        {forms.map((form) => {
          const checked = selectedForms.some((s) => s.id.includes(form.id));

          const selectionOrder = checked
            ? selectedForms.findIndex((s) => s.id === form.id) + 1
            : undefined;

          return (
            <FormCheckboxRow
              key={form.id}
              setSelectedForms={setSelectedForms}
              setPdfViewFormId={setPdfViewFormId}
              form={form}
              formContext={formContext}
              checked={checked}
              isMultiSelect={true}
              isSingleSelectCheckBox={false}
              selectionOrder={selectionOrder}
            />
          );
        })}
        {pdfViewFormId && (
          <FormContextPdfViewer
            inModal={true}
            forms={forms.filter((f) => f.id === pdfViewFormId)}
            setFormId={setPdfViewFormId}
            formId={pdfViewFormId}
          />
        )}
      </Box>
      {(h3NotSelected ||
        noApplicationSelected ||
        moreThanOneApplicationSelected ||
        differentSectionsSelected) &&
        selectedForms.length > 0 &&
        showWarningBanner && (
          <Banner
            body="It looks like you have selected an unusual combination of forms, please make sure you have selected the correct forms."
            bannerType={BannerList.WARNING}
            sx={{ mb: 2 }}
          />
        )}
      <FormFooterSection
        onSave={() => {
          const onSave = async () => {
            if (
              mode === "upload" &&
              uploadFormPdfViaExternalPatientLink &&
              uploadToRioExternalLink
            ) {
              await uploadFormPdfViaExternalPatientLink(
                uploadToRioExternalLink,
                selectedForms.map((s) => s.id),
                formContext,
              );
            }
            const firstSelectedForm = selectedForms[0];
            if (mode === "download" && firstSelectedForm !== undefined) {
              const result = await api.forms.getPdf(
                firstSelectedForm.id,
                false,
                selectedForms.slice(1).map((s) => s.id),
              );
              if (result.status === 200 && result.data) {
                const dataUri = `data:application/pdf;base64,${result.data}`;
                triggerDownload(dataUri, "form.pdf");
                renderSuccessToast({
                  message: "Forms downloaded successfully",
                });
              } else {
                renderErrorToast({
                  message: "Failed to download forms",
                });
              }
            }
            onClose();
          };
          void onSave();
        }}
        onCancel={onClose}
        saveLabel={mode === "upload" ? "Upload" : "Download"}
        discardLabel="Close"
        disableSubmit={selectedForms.length === 0}
      ></FormFooterSection>
    </PopupDialog>
  );
};
